﻿import { useState } from 'react';
import { Star } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@foundation/components/ui/dialog';
import { Button } from '@foundation/components/ui/button';
import { Textarea } from '@foundation/components/ui/textarea';
import { useToast } from '@foundation/hooks/use-toast';
import { submitPolicyFeedback } from '@/features/revisr/services/policyFeedbackService';

export type PolicyFeedbackModalProps = {
  open: boolean;
  policyId: string;
  userId: string;
  orgId?: string;
  onClose: () => void;
  /** Fired only when feedback is actually recorded (submitted or already exists) — NOT on skip/dismiss. */
  onSubmitted?: () => void;
};

const COMMENT_MAX = 2000;
const REVIEW_MAX = 4000;

export const PolicyFeedbackModal = ({ open, policyId, userId, orgId, onClose, onSubmitted }: PolicyFeedbackModalProps) => {
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [qualityReview, setQualityReview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating < 1 || submitting) return;
    setSubmitting(true);
    const result = await submitPolicyFeedback(
      { policyId, rating, comment: comment.trim() || undefined, qualityReview: qualityReview.trim() || undefined },
      userId,
      orgId,
    );
    setSubmitting(false);
    if (result === 'ok') { onSubmitted?.(); toast({ title: 'Thanks for your feedback!' }); onClose(); return; }
    if (result === 'duplicate') { onSubmitted?.(); onClose(); return; }
    toast({ title: 'Could not submit feedback', description: 'Please try again.', variant: 'destructive' });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>How was this policy?</DialogTitle>
          <DialogDescription>Your feedback helps us improve policy generation.</DialogDescription>
        </DialogHeader>
        <div className="flex gap-1" role="radiogroup" aria-label="Star rating">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
              onMouseEnter={() => setHover(n)}
              onMouseLeave={() => setHover(0)}
              onClick={() => setRating(n)}
              onKeyDown={(e) => {
                if (e.key === 'ArrowRight') setRating(Math.min(5, (rating || 0) + 1));
                if (e.key === 'ArrowLeft') setRating(Math.max(1, (rating || 1) - 1));
              }}
              className="p-1"
            >
              <Star className={(hover || rating) >= n ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
            </button>
          ))}
        </div>
        <Textarea
          value={comment}
          maxLength={COMMENT_MAX}
          placeholder="Anything you'd like us to know? (optional)"
          onChange={(e) => setComment(e.target.value.slice(0, COMMENT_MAX))}
        />
        <div className="text-xs text-muted-foreground text-right">{comment.length}/{COMMENT_MAX}</div>
        <div className="space-y-1">
          <label htmlFor="policy-quality-review" className="text-sm font-medium">
            What could be improved about this policy?
          </label>
          <p className="text-xs text-muted-foreground">
            Saved to your organisation's memory and used to improve future policies generated for this organisation.
          </p>
          <Textarea
            id="policy-quality-review"
            value={qualityReview}
            maxLength={REVIEW_MAX}
            placeholder="Tell us what needs improvement so we can raise the quality of future policies… (optional)"
            onChange={(e) => setQualityReview(e.target.value.slice(0, REVIEW_MAX))}
          />
          <div className="text-xs text-muted-foreground text-right">{qualityReview.length}/{REVIEW_MAX}</div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Skip</Button>
          <Button onClick={handleSubmit} disabled={rating < 1 || submitting}>
            {submitting ? 'Submitting…' : 'Submit'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
